/**
 * Runtime platform checks. Kept free of imports so any module can use them.
 *
 * `window.desktop` is injected by electron/preload.ts, so it exists only inside the Electron app
 * (dev and built). `window.Capacitor` is injected by the Capacitor native runtime, so it reports
 * a native platform only inside the Android app. Use `import.meta.env.VITE_PLATFORM`
 * ('web' | 'desktop' | 'android') instead where the decision must be made at build time (e.g. to
 * keep code out of the web bundle).
 */
export const isDesktopApp = () => !!window.desktop

type CapacitorGlobal = { getPlatform?: () => string, isNativePlatform?: () => boolean }

export const isAndroidApp = () => {
  const capacitor = (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor
  return !!capacitor?.isNativePlatform?.() && capacitor.getPlatform?.() === 'android'
}

/** Installed app (desktop or Android) rather than the website */
export const isNativeApp = () => isDesktopApp() || isAndroidApp()

/**
 * Whether the app may offer purchases (plans, maker pass, shop models, tips). The Android app
 * sells nothing for now (Google Play payment rules, see docs/android_app.md): it shows
 * <PurchaseUnavailable /> instead. Plans bought on the web still apply through the account.
 */
export const canPurchaseInApp = () => !isAndroidApp()
